import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { PaymentRecordDto } from '../../dtos/payment-record.dto';
import { PaymentActionEnum } from '../../enums/payment-action.enum';
import { PaymentStatusEnum } from '../../enums/payment-status.enum';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: PaymentRecordDto,
	action: PaymentActionEnum,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	const fixedStatuses = [
		PaymentStatusEnum.RETRYING,
		PaymentStatusEnum.CANCELLED,
		PaymentStatusEnum.COMPLETED,
	];

	switch (action) {
		case PaymentActionEnum.CANCEL:
			validationResult = setStatusSchema(
				PaymentStatusEnum.CANCELLED,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case PaymentActionEnum.RETRY:
			validationResult = setStatusSchema(
				PaymentStatusEnum.RETRYING,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case PaymentActionEnum.COMPLETE:
			validationResult = setStatusSchema(
				PaymentStatusEnum.COMPLETED,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		default:
			throw new Error(`Unknown action: ${action}`);
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
